import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import LoadingModal from '../../components/CustomModal/LoadingModal';
import CalculatorVersionSelector from '../../components/CalculatorForm/FieldTypes/CalculatorVersionSelector';

import '../../components/Footer/Footer';
import '../../components/NavBar/NavBar';
import { config } from '../../constants';

import './Calculator.css';
import Outputs from '../../components/Outputs/Outputs';
import CalculatorForm from '../../components/CalculatorForm/CalculatorForm';
import useCalculatorData, { fetchSystemDesign } from '../../api/useCalculatorData';
import useSaveQuote from '../../api/useSaveQuote';
import useCalculatorsVersion from '../../api/useCalculatorsVersion';
import { CircularProgress, List } from '@mui/material';
import { MIN_BESS_DEV_FEE, MIN_DEV_FEE, MIN_DEV_FEE_NOM, MIN_REDUCED_DEV_FEE_NOM } from '../../components/CalculatorForm/helpers';
import { isSalesDesign, isFinanceUser, isDevUser , canUseReducedDevFee} from '../../utils';

function Calculator() {
  const sf_host = process.env.REACT_APP_SF_HOST;
  const [calculatorVersion, setSelectedCalculatorVersion] = useState(undefined);
  const [inputs, setInputs] = useState(undefined);
  const [outputs, setOutputs] = useState(undefined);
  const [lastAction, setLastAction] = useState(undefined);
  const [isSDSales, setIsSDSales] = useState(false);
  // System design the current outputs were computed against, when it differs
  // from the selected one (i.e. the Dev Fee solver was re-run on a new SD).
  const [outputsSd, setOutputsSd] = useState(null);
  const devFeeRerunRef = useRef(null);

  const [outputDestination, setOutputDestination] = useState();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const [searchParams, ] = useSearchParams();
  const action = searchParams.get('action') || 'new'; // view, edit or clone
  const selectedQuoteId = searchParams.get('id'); // existing
  const selectedSystemDesign = searchParams.get('systemDesign'); // existing system design
  const selectedOpportunityId = searchParams.get('opportunityId'); // new sales quote
  const quoteId = searchParams.get('quoteId'); // new sales quote
  let selectedBusinessName = useRef(searchParams.get('businessName') || 'No Business Name');
  let projectId = useRef(searchParams.get('projectId') || 'No Project ID');
  const [showOutputs, setShowOutputs] = useState(false);
  const {
    data: { systemDesignData, opportunityData, selectedQuoteData, previousFormData } = {},
    loading: loadingData,
    isFetched: isFetchedData
  } = useCalculatorData(selectedSystemDesign, selectedQuoteId, selectedOpportunityId);
  const { data: availableCalcs, loading: loadingCalcs} = useCalculatorsVersion();
  const saveQuoteMutation = useSaveQuote();
  function renderHeaderName() {
    if (action === 'view') {
      return "View";
    } else if (action === 'edit') {
      return "Edit";
    } else if (action === 'clone') {
      return "Clone";
    } else {
      return "New";
    }
  }
  if(!loadingCalcs && availableCalcs && availableCalcs.length > 0 && !calculatorVersion) {
    const versionParam = searchParams.get('version');
    const firstVersion = availableCalcs[0].version.replace('.xlsm', '');
    if (versionParam && availableCalcs.some(c => c.version.replace('.xlsm', '') === versionParam)) {
      setSelectedCalculatorVersion(versionParam);
    } else {
      setSelectedCalculatorVersion(firstVersion);
    }
  }

  useEffect(() => {
    async function getSystemType() {
      if (!systemDesignData) return;
      const result = await isSalesDesign(systemDesignData);
      setIsSDSales(result);
    }
    getSystemType();
  }, [systemDesignData]);

  useEffect(() => {
    setShowOutputs(false);
    setOutputs(undefined);
    setOutputDestination(undefined);
    setLastAction(undefined);
    setOutputsSd(null);
  }, [selectedSystemDesign]);

  if (loadingData || !isFetchedData)
    return <CircularProgress color='primary' />
  function renderLoadingModal() {
    return (
      <>
        <LoadingModal
          show={loading || loadingData}
          rotateNames={lastAction !== undefined && !saveQuoteMutation.isPending}
        />
      </>
    );
  }

  const disableSave = () => {

    const isLastActionUndefined = lastAction === undefined;
    const areOutputsEmpty = outputs.length === 0;

    return isLastActionUndefined || areOutputsEmpty;
  }

  // After the EPC solver creates a new system design, re-run the Dev Fee solver
  // against it so downstream values reflect its Solar / BESS cost split.
  const rerunDevFeeOnNewSd = async (sd) => {
    const data = await fetchSystemDesign(sd.id);
    if (data?.error) {
      throw new Error(data.error);
    }
    await devFeeRerunRef.current({ id: sd.id, data });
    setOutputsSd(sd);
  };

  const handleLastActionChange = (newAction) => {
    setLastAction(newAction);
    setOutputsSd(null);
  };

  // export to salesforce
  const handleSave = async (sdOverride = outputsSd) => {
    // Quotes are only saved against a system design
    if (!selectedSystemDesign && !sdOverride) {
      toast.error('A system design is required to save a quote.', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }
    setLoading(true);
    let url = '/api/export-to-sf';
    if (lastAction !== undefined) {
      url = url + "?action=" + lastAction;
    }
    url = url + '&version=' + calculatorVersion;
    if (action === 'edit' && !sdOverride) {
      url = url + "&id=" + selectedQuoteId;
    }

    // Check if the dev fee is below the minimum threshold
    const solverKWDevFee = outputs.find(output => output.data_variable_element === 'sr_finl_dev_fee_per_watt_out')?.value;
    const solverKWHDevFee = outputs.find(output => output.data_variable_element === 'sr_finl_dev_fee_per_kwh_out')?.value;
    const project_type = systemDesignData.project_type__c || 'Solar-only';
    const solverDevFee = project_type === 'Storage-only' ? solverKWHDevFee : solverKWDevFee;
    const min_dev_fee = project_type === 'Storage-only' ? MIN_BESS_DEV_FEE : MIN_DEV_FEE;

    // solverDevFee is a string like "$0.1845"
    const parsedDevFee = typeof solverDevFee === 'string' ? parseFloat(solverDevFee.replace('$', '')) : solverDevFee || 0;

    if ((parsedDevFee < min_dev_fee) && !isFinanceUser() && !isDevUser()) {
      toast.error(`Note: Only Finance and Development can save quotes with a dev fee less than the minimum dev fee $${min_dev_fee.toLocaleString()}. Please adjust the dev fee or contact Finance or Development.`, {
        position: toast.POSITION.TOP_RIGHT
      });
      setLoading(false);
      return;
    }

    if (!inputs.skip_minimum_dev_fee_check) {
      const sr_finl_dev_fee_nom = outputs.find(output => output.data_variable_element === 'sr_finl_dev_fee_nom')?.value;
      const parsedDevFeeNom = typeof sr_finl_dev_fee_nom === 'string' ? parseFloat(sr_finl_dev_fee_nom.replace('$', '').replace(',', '')) : sr_finl_dev_fee_nom || 0;
      const min_dev_fee_nom = canUseReducedDevFee() && ["Solar-only"].includes(inputs.project_type) ? MIN_REDUCED_DEV_FEE_NOM : MIN_DEV_FEE_NOM;
      if ((parsedDevFeeNom < min_dev_fee_nom) && !isFinanceUser()) {
        toast.error(`Note: Only Finance can save quotes with a nominal dev fee less than the minimum nominal dev fee $${min_dev_fee_nom.toLocaleString()}. Please adjust the dev fee or contact Finance.`, {
          position: toast.POSITION.TOP_RIGHT
        });
        setLoading(false);
        return;
      }
    }

    const {
      referral_partner_company_name,
      developer_partner_company_name,
      channel_epc_cost_referral_pcnt,
      channel_epc_cost_referral_per_watt,
      channel_finl_dev_fee_referral_pcnt,
      channel_partner_channel_referral_deal_type,
      developer_partner_channel_referral_deal_type,
      referral_partner_channel_referral_deal_type,
      channel_partner_company_name,
    } = previousFormData;

    if (!referral_partner_company_name || !developer_partner_company_name || !channel_partner_company_name) {
      toast.error('The quote must have referral partner, developer partner and channel partner company names. Please add them.', {
        position: toast.POSITION.TOP_RIGHT
      });
      setLoading(false);
      return;
    }

    if (!referral_partner_channel_referral_deal_type || !developer_partner_channel_referral_deal_type) {
      toast.error('The quote must have both referral partner channel referral deal type and developer partner channel referral deal type defined. Please add them.', {
        position: toast.POSITION.TOP_RIGHT
      });
      setLoading(false);
      return;
    }

    if (!channel_partner_company_name.toLowerCase().includes('sunrock')) {
      const epc_pcnt = parseFloat(channel_epc_cost_referral_pcnt);
      const epc_per_watt = parseFloat(channel_epc_cost_referral_per_watt);
      const final_dev_fee_pcnt = parseFloat(channel_finl_dev_fee_referral_pcnt);

      if ((epc_pcnt === 0 && epc_per_watt === 0) || final_dev_fee_pcnt === 0) {
        toast.error(
          'The quote must have a valid referral configuration. Channel EPC cost referral percent or per watt must be provided (at least one), and channel final dev fee referral percent must be non-zero.',
          { position: toast.POSITION.TOP_RIGHT }
        );
        setLoading(false);
        return;
      }
    }

    if (channel_partner_channel_referral_deal_type?.toLowerCase().includes('sunstone') ||
        referral_partner_channel_referral_deal_type?.toLowerCase().includes('sunstone') ||
        developer_partner_channel_referral_deal_type?.toLowerCase().includes('sunstone')) {
      if (!channel_partner_company_name?.toLowerCase().includes('sunstone')) {
        toast.error('The channel partner name must include "Sunstone" if the channel partner or referral partner deal type includes "Sunstone".', {
          position: toast.POSITION.TOP_RIGHT
        });
        setLoading(false);
        return;
      }
    }

    const output_epc_total_per_watt_sd = Number(outputs.find(output => output.data_variable_element === 'epc_total_per_watt_sd')?.real_value);
    const input_epc_total_per_watt_sd = Number(previousFormData.epc_total_per_watt_sd);

    const output_epc_bess_per_kwh_sd = Number(outputs.find(output => output.data_variable_element === 'epc_bess_per_kwh_sd')?.real_value);
    const input_epc_bess_per_kwh_sd = Number(previousFormData.epc_bess_per_kwh_sd);

    let epc_name;
    let input_epc_per_unit_sd;
    let output_epc_per_unit;

    if (project_type === 'Storage-only') {
      epc_name = "BESS EPC ($/kWh)";
      input_epc_per_unit_sd = input_epc_bess_per_kwh_sd;
      output_epc_per_unit =  output_epc_bess_per_kwh_sd;
    } else{
      epc_name = "EPC ($/W)";
      input_epc_per_unit_sd = input_epc_total_per_watt_sd
      output_epc_per_unit =  output_epc_total_per_watt_sd;
    }

    if (!sdOverride) {
      const engDesignChanged = !isSDSales && (output_epc_per_unit != 0 && Math.abs(output_epc_per_unit - input_epc_per_unit_sd) > 0.00005);
      const salesDesignChanged = isSDSales && Math.abs(output_epc_per_unit - input_epc_per_unit_sd) > 0.00005;
      const errorChangeEPCMessage = `EPC costs have been adjusted and this quote cannot be saved.
        The output ${epc_name}: $${output_epc_per_unit.toFixed(4)}
        differs from the input system design ${epc_name}: $${input_epc_per_unit_sd.toFixed(4)}.
        To save a quote with a different EPC cost, update the cost in the Salesforce system design.`;
      if (engDesignChanged || salesDesignChanged) {
          toast.error(
            errorChangeEPCMessage,
            { position: toast.POSITION.TOP_RIGHT }
          );
          setLoading(false);
          return;
      }
    }

    await saveQuoteMutation.mutateAsync({
      inputs: sdOverride ? { ...inputs, system_design_name: sdOverride.name } : inputs,
      selectedSystemDesign: sdOverride ? sdOverride.id : selectedSystemDesign,
      selectedOpportunityId: selectedOpportunityId,
      outputDestination: outputDestination,
      url: url
    }, {
      onSuccess: (data) => {
        toast.success('Successfully created sales quote ' +
          (action === 'edit' ? selectedQuoteId : data.id), {
          position: toast.POSITION.TOP_RIGHT
        });
        clearFormData();
        setLoading(false);
      },
      onError: (error) => {
        toast.warning('Failed to create sales quote, retry. Error message is: ' + error.toString(), {
            position: toast.POSITION.TOP_RIGHT
          });
        setLoading(false);
      }
    });
  }

  const clearFormData = () => {
    setShowOutputs(false);
    setOutputs(undefined);
    setOutputDestination(undefined);
    setOutputsSd(null);
  }

  // change status to engineer review
  const handleSubmitToEng = () => {
    setLoading(true);
    axios.patch('/api/quotes?id=' + selectedQuoteId, config)
      .then(data => {
        setLoading(false);
        if (data.status === 200) {
          toast.success('Successfully submitted quote to engineering review', {
            position: toast.POSITION.TOP_RIGHT
          });
          navigate('/');
        } else {
          toast.warning('Failed to update sales quote, retry', {
            position: toast.POSITION.TOP_RIGHT
          });
        }
      }).catch(function() {
        setLoading(false);
        toast.warning('Failed to update sales quote, retry', {
          position: toast.POSITION.TOP_RIGHT
        });
      });
  }

  function getOpportunityLink(opportunity) {
    const id = opportunity?.Id;
    const name = opportunity?.Name;
    if (sf_host.includes('http')) {
      return (
        <a
          target="_blank"
          rel="noreferrer"
          href={`${sf_host}/lightning/r/Opportunity/${id}/view`}
        >
          {name}
        </a>
      )
    } else
      return (
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://${sf_host}/lightning/r/Opportunity/${id}/view`}
        >
          {name}
        </a>
      )
  }

  function getQuoteLink(quoteId) {
    return (
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://${sf_host}/lightning/r/Quote/${quoteId}/view`}
      >
        {quoteId}
      </a>
    )
  }

  if (!opportunityData)
    return <div>Loading...</div>

  return (
    <div style={{ overflow: 'auto' }}>
      {renderLoadingModal()}
      <Container style={{ height: '1100px' }}>
        {selectedQuoteId ? <p style={{ marginTop: '40px', marginBottom: '20px' }}>
          <Link to='/' style={{ color: '#FF7E0B' }}>Opportunities</Link> / &nbsp;
          {getQuoteLink(selectedQuoteId)} : &nbsp;
          {getOpportunityLink(opportunityData)} / {opportunityData && opportunityData.StageName} / {!!quoteId && `Quote #${quoteId}`} / {calculatorVersion}</p> : undefined}
        <h2 style={{ marginTop: '40px', marginBottom: '20px' }}>{renderHeaderName()} Sales Quote</h2>
        {action !== 'view' ? (<><Row className='add-space section-white'>
          <Col>
            <p>Calculator Version</p>
            <CalculatorVersionSelector
              availableCalcs={availableCalcs}
              calculatorVersion={calculatorVersion}
              setSelectedCalculatorVersion={setSelectedCalculatorVersion}
              action={action}
            />
          </Col>
          <Col md="auto" className='add-space'>
            {action === 'new' || action === 'clone' || (opportunityData && opportunityData.StageName === 'Engineer Review') ? undefined :
              <Button variant="success" size="lg" onClick={() => handleSubmitToEng()}>Submit to Engineering</Button>}
          </Col>
          <Col style={{"display": "flex", "flexFlow": "row-reverse"}}>
            {getOpportunityLink(opportunityData)}
          </Col>
        </Row></>) : undefined}
        <Row className='add-space'>
          <Col>
            <Card sx={{ marginBottom: 7}}>
              <CardContent sx={{ height: '80vh'}}>
                <List style={{maxHeight: '100%', overflow: 'auto'}}>
                    {calculatorVersion && opportunityData &&
                    (action === 'view' ? selectedQuoteData : true) &&
                    (action === 'edit' ? selectedQuoteData : true) &&
                    (action === 'clone' ? selectedQuoteData : true) &&
                    (selectedSystemDesign ? (!loadingData && isFetchedData ) : true) &&
                        <CalculatorForm
                          action={action}
                          systemDesignData={systemDesignData}
                          opportunityData={opportunityData}
                          opportunityId={selectedOpportunityId}
                          prevFormData={locationState?.preservedInputs || previousFormData}
                          outputDestination={outputDestination}
                          setOutputDestination={setOutputDestination}
                          setInputs={setInputs}
                          selectedSystemDesign={selectedSystemDesign}
                          opportunityState={opportunityData.project_state__c}
                          calculatorVersion={calculatorVersion}
                          setOutputs={setOutputs}
                          setShowOutputs={setShowOutputs}
                          setLastAction={handleLastActionChange}
                          sdIsEngineered={!isSDSales}
                          devFeeRerunRef={devFeeRerunRef}
                      />}
                </List>
              </CardContent>
            </Card>
          </Col>
          <Col>
            <Card>
              <CardContent sx={{ height: '80vh'}} className='outputs-section' h>
              { calculatorVersion && opportunityData &&
                <Outputs systemDesignData={systemDesignData}
                    selectedSystemDesign={selectedSystemDesign}
                    outputs={outputs}
                    isView={action === 'view'}
                    action={action}
                    showOutputs={showOutputs}
                    outputDestination={outputDestination}
                    calculatorVersion={calculatorVersion}
                    businessName={selectedBusinessName.current}
                    projectId={projectId.current}
                    onOutputsChange={setOutputs}
                    prevFormData={previousFormData}
                    quoteData={selectedQuoteData}
                    disableSave={disableSave}
                    handleSave={() => handleSave()}
                    selectedFinancingType={(inputs  && inputs?.financing_type) || previousFormData?.financing_type}
                    opportunityData={opportunityData}
                    opportunityId={selectedOpportunityId}
                    isSDSales={isSDSales}
                    onNewSdCreated={rerunDevFeeOnNewSd}
                    outputsSd={outputsSd}
                    currentInputs={inputs ? { ...previousFormData, ...inputs } : undefined}
                  />}
              </CardContent>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );

}

export default Calculator;
